import { Component } from '@theme/component';

/**
 * A custom element that manages product discount slider.
 * Handles dynamic discount calculation and UI updates based on slider position.
 *
 * @typedef {object} ProductDiscountSliderRefs
 * @property {HTMLInputElement} slider - The range slider input element.
 * @property {HTMLInputElement} quantityInput - The quantity input element.
 * @property {HTMLElement} priceDisplay - The element displaying the discounted price.
 * @property {HTMLElement} discountDisplay - The element displaying the discount percentage.
 * @property {HTMLElement} discountLabels - The container for discount percentage labels.
 * @property {HTMLScriptElement} discountSliderData - The script element containing discount data.
 *
 * @extends Component<ProductDiscountSliderRefs>
 */
export class ProductDiscountSlider extends Component {
  requiredRefs = ['discountSliderData'];

  /** @type {{ basePrice: number; discountArray: number[]; minQuantity: number; maxQuantity: number; quantityStep: number } | null} */
  #data = null;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    // Parse JSON data from script element
    const { discountSliderData } = this.refs;
    if (discountSliderData instanceof HTMLScriptElement) {
      try {
        const parsed = JSON.parse(discountSliderData.textContent || '{}');
        this.#data = {
          basePrice: parseFloat(parsed.basePrice) || 0,
          discountArray: parsed.discountArray || [],
          minQuantity: parseInt(parsed.minQuantity) || 100,
          maxQuantity: parseInt(parsed.maxQuantity) || 100000,
          quantityStep: parseInt(parsed.quantityStep) || 10,
        };
      } catch (error) {
        console.error('Failed to parse discount slider data:', error);
        return;
      }
    }

    if (!this.#data) return;

    const slider = /** @type {HTMLInputElement | undefined} */ (this.refs.slider);
    const quantityInput = /** @type {HTMLInputElement | undefined} */ (this.refs.quantityInput);

    // Attach event listeners
    this.#abortController = new AbortController();

    if (slider instanceof HTMLInputElement) {
      slider.addEventListener('input', this.#handleSliderChange, {
        signal: this.#abortController.signal,
      });
    }

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.addEventListener('input', this.#handleQuantityChange, {
        signal: this.#abortController.signal,
      });
    }

    // Hide/show discount-related UI elements based on discountArray
    this.#toggleDiscountUI();

    // Initial update
    this.#updateSlider();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
  }

  /**
   * Handles slider input change event
   * @param {Event} event - The input event
   */
  #handleSliderChange = (event) => {
    event.stopPropagation();

    const slider = /** @type {HTMLInputElement | undefined} */ (this.refs.slider);
    const quantityInput = /** @type {HTMLInputElement | undefined} */ (this.refs.quantityInput);
    if (!(slider instanceof HTMLInputElement)) return;

    const value = parseInt(slider.value);

    // Update quantity input
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = value.toString();
    }

    this.#updateSlider();
  };

  /**
   * Handles quantity input change event
   * @param {Event} event - The input event
   */
  #handleQuantityChange = (event) => {
    event.stopPropagation();

    const slider = /** @type {HTMLInputElement | undefined} */ (this.refs.slider);
    const quantityInput = /** @type {HTMLInputElement | undefined} */ (this.refs.quantityInput);
    if (!(quantityInput instanceof HTMLInputElement) || !(slider instanceof HTMLInputElement) || !this.#data) return;

    let value = parseInt(quantityInput.value) || this.#data.minQuantity;

    // Clamp value between min and max
    value = Math.max(this.#data.minQuantity, Math.min(this.#data.maxQuantity, value));

    // Update slider and input
    slider.value = value.toString();
    quantityInput.value = value.toString();

    this.#updateSlider();
  };

  /**
   * Toggle visibility and render discount labels
   */
  #toggleDiscountUI() {
    if (!this.#data) return;

    const hasDiscount = this.#data.discountArray.length > 0;
    const discountLabels = /** @type {HTMLElement | undefined} */ (this.refs.discountLabels);
    const discountDisplay = /** @type {HTMLElement | undefined} */ (this.refs.discountDisplay);

    // Render discount labels dynamically based on discountArray
    if (discountLabels instanceof HTMLElement) {
      if (hasDiscount) {
        // Clear existing labels
        discountLabels.innerHTML = '';

        // Render each discount percentage as a label
        this.#data.discountArray.forEach((discount) => {
          const label = document.createElement('span');
          label.className = 'text-xs font-semibold text-slate-600';
          label.textContent = `-${discount}%`;
          discountLabels.appendChild(label);
        });

        discountLabels.style.display = '';
      } else {
        discountLabels.style.display = 'none';
        discountLabels.innerHTML = '';
      }
    }

    // Hide discount display if no discount array
    if (discountDisplay instanceof HTMLElement) {
      discountDisplay.style.display = hasDiscount ? '' : 'none';
    }
  }

  /**
   * Updates the slider gradient, price, and discount
   */
  #updateSlider() {
    const slider = /** @type {HTMLInputElement | undefined} */ (this.refs.slider);
    const priceDisplay = /** @type {HTMLElement | undefined} */ (this.refs.priceDisplay);
    const discountDisplay = /** @type {HTMLElement | undefined} */ (this.refs.discountDisplay);

    if (!(slider instanceof HTMLInputElement) || !this.#data) return;

    const quantity = parseInt(slider.value);
    const percentage = ((quantity - this.#data.minQuantity) / (this.#data.maxQuantity - this.#data.minQuantity)) * 100;

    // Update slider background gradient
    slider.style.background = `linear-gradient(to right, rgb(59, 130, 246) 0%, rgb(59, 130, 246) ${percentage}%, rgb(254, 215, 170) ${percentage}%, rgb(254, 215, 170) 100%)`;

    // Calculate discount based on quantity
    const discount = this.#calculateDiscount(quantity);
    const hasDiscount = this.#data.discountArray.length > 0 && discount > 0;
    
    // Calculate total price: basePrice × quantity × (1 - discount/100)
    const totalPrice = hasDiscount 
      ? this.#data.basePrice * quantity * (1 - discount / 100)
      : this.#data.basePrice * quantity;

    // Update price display
    if (priceDisplay instanceof HTMLElement) {
      priceDisplay.textContent = this.#formatMoney(totalPrice);
    }

    // Update discount display - hide if no discount
    if (discountDisplay instanceof HTMLElement) {
      if (hasDiscount) {
        discountDisplay.textContent = `(-${discount}%)`;
        discountDisplay.style.display = '';
      } else {
        discountDisplay.style.display = 'none';
      }
    }

    // Dispatch custom event with updated values
    this.dispatchEvent(
      new CustomEvent('product-discount-slider:update', {
        bubbles: true,
        detail: {
          quantity,
          discount,
          totalPrice,
          unitPrice: this.#data.basePrice,
          hasDiscount,
        },
      })
    );
  }

  /**
   * Calculate discount percentage based on quantity
   * @param {number} quantity - The quantity value
   * @returns {number} Discount percentage
   */
  #calculateDiscount(quantity) {
    if (!this.#data || this.#data.discountArray.length === 0) return 0;

    // Define quantity ranges for each discount level
    const range = this.#data.maxQuantity - this.#data.minQuantity;
    const step = range / this.#data.discountArray.length;

    // Find the appropriate discount tier
    for (let i = 0; i < this.#data.discountArray.length; i++) {
      const threshold = this.#data.minQuantity + step * (i + 1);
      if (quantity <= threshold) {
        return this.#data.discountArray[i];
      }
    }

    // Return highest discount if quantity exceeds all thresholds
    return this.#data.discountArray[this.#data.discountArray.length - 1];
  }

  /**
   * Format price as money string using Shopify's money format
   * @param {number} price - The price value
   * @returns {string} Formatted price string
   */
  #formatMoney(price) {
    // Get Shopify money format if available
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      const cents = Math.round(price * 100);
      return Shopify.formatMoney(cents);
    }

    // Fallback to simple formatting
    return `$${price.toFixed(2)}`;
  }
}

if (!customElements.get('product-discount-slider')) {
  customElements.define('product-discount-slider', ProductDiscountSlider);
}
